import {
  choiceInputClass,
  choiceLabelClass,
  describedBy,
  FieldError,
  HelpText,
  groupLabelClass,
  isRequiredNow,
  RequiredMark,
  wrapperProps,
  type FieldProps,
} from "./shared";

/**
 * The port of fragments/forms/fields/radio-group.php.
 *
 * A group of choices is a group, so the wrapper says so with role="group" and
 * names itself from the question: screen readers read the question with each
 * option instead of leaving "Yes" and "No" to stand on their own. The controls
 * are real radios, styled.
 *
 * A group may accept only one of its answers - a validation "equals" rule says
 * which. That is published as data-equals with the copy for refusing the
 * others, and the step validator enforces it exactly as it enforces "required".
 *
 * The PHP also put aria-required and aria-invalid on each radio, which the
 * radio role does not support; the native required attribute carries it.
 */
export default function RadioGroupField(props: FieldProps) {
  const { id, name, field, value, error, shown, onChange, onFocus } = props;

  if (
    !id ||
    !name ||
    !field ||
    !field.options ||
    !Object.keys(field.options).length
  ) {
    return null;
  }

  const required = isRequiredNow(field, shown);
  const equals = field.validation?.equals ?? "";
  const equalsMessage =
    field.validation?.messages?.equals ||
    "Please choose a different answer to continue.";

  return (
    <div
      {...wrapperProps(props)}
      data-equals={equals !== "" ? equals : undefined}
      data-equals-message={equals !== "" ? equalsMessage : undefined}
      role="group"
      aria-labelledby={`${id}-label`}
    >
      <h3 className={groupLabelClass} id={`${id}-label`}>
        {field.label} <RequiredMark field={field} />
      </h3>

      <HelpText id={id} field={field} />

      <div className="flex flex-col gap-2.5">
        {Object.entries(field.options).map(([optionValue, optionLabel]) => {
          // sanitize_html_class(), as the PHP built the option ids with.
          const optionId = `${id}-${optionValue.replace(/[^A-Za-z0-9_-]/g, "")}`;

          return (
            <div key={optionValue} className="flex items-center gap-2.5">
              <input
                className={`${choiceInputClass} js-field-el`}
                type="radio"
                id={optionId}
                name={name}
                value={optionValue}
                checked={value === optionValue}
                required={required}
                aria-describedby={describedBy(id, field)}
                data-validate="1"
                data-required={required ? "1" : undefined}
                onChange={(event) => onChange(name, event.target.value)}
                onFocus={() => onFocus(name)}
              />

              <label className={choiceLabelClass} htmlFor={optionId}>
                {optionLabel}
              </label>
            </div>
          );
        })}
      </div>

      <FieldError id={id} error={error} />
    </div>
  );
}
